import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Card, Field, HeroSelectField, LoadingScreen, PageHeader, Textarea } from '../../components/Ui';
import { useToast } from '../../contexts/ToastContext';
import { useBusinessBranches, useImportInventory } from '../../hooks/businessData';
import { apiUrl, getStoredToken } from '../../lib/api';
import type { InventoryImportResult } from '../../../shared/types';
import { InventoryTabs } from './InventoryTabs';

const csvExample = 'external_code,stock_cantidad\nBURGER-01,12\nSODA-02,30';
const jsonExample = '{"items":[{"externalCode":"BURGER-01","stockQuantity":12}]}';

export default function InventoryImportPage() {
  const { show } = useToast();
  const { data: branches = [], isLoading } = useBusinessBranches();
  const [branchId, setBranchId] = useState('');
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [body, setBody] = useState(csvExample);
  const [result, setResult] = useState<InventoryImportResult | null>(null);
  const importInventory = useImportInventory();
  const selectedBranchId = branchId || branches[0]?.id || '';

  const downloadCsv = async () => {
    if (!selectedBranchId) return;
    try {
      const headers = new Headers();
      const token = getStoredToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      const response = await fetch(apiUrl(`/api/business/inventory/export?branchId=${encodeURIComponent(selectedBranchId)}`), { headers });
      if (!response.ok) throw new Error('No se pudo exportar el inventario.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory-${selectedBranchId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo exportar el inventario.', 'error');
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedBranchId) return;
    try {
      const next = await importInventory.mutateAsync({ branchId: selectedBranchId, body, contentType: format === 'csv' ? 'text/csv' : 'application/json' });
      setResult(next);
      show('Importación procesada.', 'success');
    } catch (error) {
      show(error instanceof Error ? error.message : 'No se pudo importar el inventario.', 'error');
    }
  };

  if (isLoading) return <LoadingScreen label="Cargando sucursales…" />;

  return (
    <div>
      <PageHeader
        title="Importar inventario externo"
        description="Actualizá productos en modo externo usando external_code. Los códigos desconocidos se omiten."
      />
      <InventoryTabs />
      <Card>
        <form onSubmit={submit} className="grid gap-4">
          <HeroSelectField label="Sucursal" value={selectedBranchId || null} onChange={(value) => setBranchId(String(value ?? ''))} options={branches.map((branch) => ({ id: branch.id, label: branch.name }))} />
          <HeroSelectField label="Formato" value={format} onChange={(value) => { const next = String(value ?? 'csv') as 'csv' | 'json'; setFormat(next); setBody(next === 'csv' ? csvExample : jsonExample); }} options={[{ id: 'csv', label: 'CSV' }, { id: 'json', label: 'JSON' }]} />
          <Field label="Contenido" hint={format === 'csv' ? 'Encabezados: external_code,stock_cantidad' : 'Estructura: { items: [{ externalCode, stockQuantity }] }'}>
            <Textarea value={body} onChange={(event) => setBody(event.target.value)} rows={12} />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button type="submit" busy={importInventory.isPending} disabled={!selectedBranchId}>Importar</Button>
            <Button type="button" variant="secondary" disabled={!selectedBranchId} onPress={() => void downloadCsv()}>Descargar CSV actual</Button>
          </div>
        </form>
      </Card>
      {result ? (
        <Card className="mt-5">
          <h2 className="mb-3 text-lg font-black text-[var(--app-brand-ink)]">Resultado</h2>
          <div className="grid gap-2 text-sm text-[var(--app-brand-muted)]">
            <p>Procesados: <strong>{result.processed}</strong></p>
            <p>Actualizados: <strong>{result.updated}</strong></p>
            <p>Omitidos por código desconocido: <strong>{result.skippedUnknown}</strong></p>
            {result.warnings.map((warning) => <p key={warning} className="text-amber-700">{warning}</p>)}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
